import React, { useEffect, useState } from 'react'
import axios from 'axios'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { BookOpen } from 'lucide-react'

// Markdown → styled elements (no typography plugin needed).
const MD = {
  h1: (p) => <h1 className="text-2xl font-bold text-gray-900 mb-3" {...p} />,
  h2: (p) => <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-2 pb-1 border-b border-gray-200" {...p} />,
  h3: (p) => <h3 className="text-base font-semibold text-gray-900 mt-5 mb-1" {...p} />,
  p: (p) => <p className="text-sm text-gray-700 leading-relaxed my-3" {...p} />,
  ul: (p) => <ul className="list-disc pl-5 my-3 space-y-1 text-sm text-gray-700" {...p} />,
  ol: (p) => <ol className="list-decimal pl-5 my-3 space-y-1 text-sm text-gray-700" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  a: (p) => <a className="text-primary-600 hover:underline" {...p} />,
  strong: (p) => <strong className="font-semibold text-gray-900" {...p} />,
  blockquote: (p) => <blockquote className="border-l-4 border-amber-400 bg-amber-500/5 px-4 py-2 my-4 text-sm text-gray-700 rounded-r" {...p} />,
  hr: () => <hr className="my-6 border-gray-200" />,
  code: ({ inline, ...p }) => inline
    ? <code className="bg-gray-100 text-gray-800 rounded px-1.5 py-0.5 text-[13px] font-mono" {...p} />
    : <code className="font-mono text-[13px]" {...p} />,
  pre: (p) => <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 my-4 overflow-x-auto text-[13px] leading-relaxed" {...p} />,
  table: (p) => <div className="overflow-x-auto my-4"><table className="w-full text-sm border-collapse" {...p} /></div>,
  thead: (p) => <thead className="bg-gray-50" {...p} />,
  th: (p) => <th className="text-left font-semibold text-gray-700 border border-gray-200 px-3 py-2" {...p} />,
  td: (p) => <td className="border border-gray-200 px-3 py-2 text-gray-700 align-top" {...p} />,
}

export default function Docs() {
  const [groups, setGroups] = useState([])
  const [slug, setSlug] = useState('index')
  const [md, setMd] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    axios.get('/api/v1/docs').then(r => setGroups(r.data.groups || [])).catch(() => setGroups([]))
  }, [])

  useEffect(() => {
    setLoading(true)
    axios.get(`/api/v1/docs/${slug}`)
      .then(r => setMd(r.data.markdown || ''))
      .catch(() => setMd('# Not available\nCouldn’t load this guide.'))
      .finally(() => setLoading(false))
  }, [slug])

  return (
    <div className="flex gap-8">
      <aside className="w-60 shrink-0">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
          <BookOpen size={18} className="text-primary-500" /> Documentation
        </h2>
        <nav className="space-y-5">
          {groups.map(g => (
            <div key={g.group}>
              <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1.5">{g.group}</p>
              <div className="space-y-0.5">
                {g.items.map(it => (
                  <button key={it.slug} onClick={() => setSlug(it.slug)}
                    className={`block w-full text-left px-2.5 py-1.5 rounded-md text-sm transition-colors ${
                      slug === it.slug ? 'bg-primary-500/10 text-primary-600 font-medium' : 'text-gray-600 hover:bg-gray-50'
                    }`}>
                    {it.title}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      <article className="flex-1 min-w-0 max-w-3xl card">
        {loading
          ? <p className="text-sm text-gray-400 animate-pulse">Loading…</p>
          : <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{md}</ReactMarkdown>}
      </article>
    </div>
  )
}
